import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod/v4";
import { SlotCard } from "@/components/driver/slot-card";
import { WeekSelector } from "@/components/driver/week-selector";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useAssignSlot,
  useClaimSlot,
  useDriverTaskBoard,
  useReleaseSlot,
  useUpdateSlotStatus,
} from "@/hooks/use-driver-board";
import { useDrivers } from "@/hooks/use-drivers";
import { useProfile } from "@/hooks/use-profile";
import { useCurrentSeason, useCurrentWeek } from "@/hooks/use-setups";
import type { UserRole } from "@/lib/roles";
import { canUploadSetups, isAdmin, isEditor } from "@/lib/roles";

const searchSchema = z.object({
  week: z.coerce.number().optional(),
});

export const Route = createFileRoute("/_auth/app/dashboard/")({
  validateSearch: searchSchema,
  component: DriverTaskBoard,
});

function DriverTaskBoard() {
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { data: currentSeason } = useCurrentSeason();
  const { data: currentWeek } = useCurrentWeek(currentSeason?.id);
  const navigate = useNavigate();
  const search = Route.useSearch();

  const selectedWeek = search.week ?? currentWeek ?? 1;

  function setSelectedWeek(week: number) {
    navigate({ to: "/app/dashboard", search: { week }, replace: true });
  }

  const profileReady = !profileLoading && profile !== undefined;
  const role = (profile?.role ?? "user") as UserRole;
  const hasAccess = canUploadSetups(role);
  const adminOrEditor = isAdmin(role) || isEditor(role);

  // Redirect non-drivers
  useEffect(() => {
    if (profileReady && !hasAccess) {
      navigate({ to: "/app/setups", search: {} as any });
    }
  }, [profileReady, hasAccess, navigate]);

  const [mySlotsOnly, setMySlotsOnly] = useState(true);

  // Slot claiming hooks
  const claimMut = useClaimSlot();
  const releaseMut = useReleaseSlot();
  const assignMut = useAssignSlot();
  const statusMut = useUpdateSlotStatus();
  const { data: drivers } = useDrivers();

  function handleClaim(slotId: string) {
    if (!profile?.id) return;
    claimMut.mutate(
      { slotId, driverId: profile.id },
      {
        onSuccess: (res) => {
          if (res.success) toast.success("Slot claimed");
          else toast.error(res.error ?? "Failed to claim");
        },
      },
    );
  }

  function handleRelease(slotId: string) {
    if (!profile?.id) return;
    releaseMut.mutate(
      { slotId, driverId: profile.id },
      {
        onSuccess: (res) => {
          if (res.success) toast.success("Slot released");
          else toast.error(res.error ?? "Failed to release");
        },
      },
    );
  }

  function handleAssign(slotId: string, driverId: string | null) {
    assignMut.mutate(
      { slotId, driverId },
      {
        onSuccess: (res) => {
          if (res.success) toast.success(driverId ? "Driver assigned" : "Assignment cleared");
          else toast.error(res.error ?? "Failed to assign");
        },
      },
    );
  }

  function handleStatusChange(slotId: string, status: string) {
    statusMut.mutate(
      { slotId, status },
      {
        onSuccess: (res) => {
          if (res.success) toast.success(`Status changed to ${status.replace("_", " ")}`);
          else toast.error(res.error ?? "Failed to update status");
        },
      },
    );
  }

  const { data: taskBoard, isLoading: boardLoading } = useDriverTaskBoard(
    profile?.id,
    currentSeason?.id,
    selectedWeek,
  );

  // Filter to only show series groups with slots assigned to the current driver
  const filteredGroups = useMemo(() => {
    if (!taskBoard?.series_groups) return [];
    if (!mySlotsOnly) return taskBoard.series_groups;
    return taskBoard.series_groups
      .map((group) => ({
        ...group,
        slots: group.slots.filter((slot) => slot.is_assigned_to_me),
      }))
      .filter((group) => group.slots.length > 0);
  }, [taskBoard?.series_groups, mySlotsOnly]);

  if (!profileReady) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return null;
  }

  const totalSlots = taskBoard?.total_slots ?? 0;
  const completedSlots = taskBoard?.completed_slots ?? 0;
  const progressPercent = totalSlots > 0 ? Math.round((completedSlots / totalSlots) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-[Bebas_Neue] text-3xl text-foreground">Driver Task Board</h1>
        <p className="text-sm text-muted-foreground">
          {taskBoard?.season_name ?? currentSeason?.name ?? "Loading season..."}
        </p>
      </div>

      {/* Week selector */}
      <WeekSelector selectedWeek={selectedWeek} onWeekChange={setSelectedWeek} />

      {/* My slots toggle */}
      <div className="flex items-center gap-2">
        <Switch id="my-slots" checked={mySlotsOnly} onCheckedChange={setMySlotsOnly} />
        <Label htmlFor="my-slots" className="text-sm text-muted-foreground cursor-pointer">
          Show only my slots
        </Label>
      </div>

      {/* Progress bar */}
      {totalSlots > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {completedSlots} of {totalSlots} setups completed
            </span>
            <span className="font-medium text-foreground">{progressPercent}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
            <div
              className="h-full rounded-full bg-green-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Loading */}
      {boardLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )}

      {/* Series groups */}
      {!boardLoading && taskBoard?.series_groups && (
        <div className="space-y-8">
          {filteredGroups.map((group) => (
            <section key={group.series.id} className="space-y-3">
              {/* Series header */}
              <div className="flex items-center gap-3">
                <h2 className="font-[Bebas_Neue] text-xl text-foreground">{group.series.name}</h2>
                <span className="text-sm text-muted-foreground">{group.track.name}</span>
                {group.is_assigned && (
                  <Badge className="border-transparent bg-green-600/80 text-white text-[10px] px-1.5 py-0">
                    ASSIGNED TO YOU
                  </Badge>
                )}
              </div>

              {/* Slot cards grid */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {group.slots.map((slot) => (
                  <SlotCard
                    key={slot.id}
                    slot={slot}
                    role={role}
                    drivers={adminOrEditor ? drivers : undefined}
                    onClaim={handleClaim}
                    onRelease={handleRelease}
                    onAssign={adminOrEditor ? handleAssign : undefined}
                    onStatusChange={adminOrEditor ? handleStatusChange : undefined}
                    onClick={() => {
                      navigate({
                        to: "/app/dashboard/upload/$slotId",
                        params: { slotId: slot.id },
                        search: { week: selectedWeek },
                      });
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!boardLoading && taskBoard?.series_groups && filteredGroups.length === 0 && (
        <div className="rounded-xl border border-dashed border-border/50 py-16 text-center">
          <p className="text-muted-foreground">
            {mySlotsOnly && taskBoard.series_groups.length > 0
              ? "No slots assigned to you for this week."
              : `No setup slots for week ${selectedWeek}.`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground/70">
            {mySlotsOnly && taskBoard.series_groups.length > 0
              ? "Turn off the filter to see all slots."
              : "No active series have tracks assigned for this week."}
          </p>
        </div>
      )}
    </div>
  );
}
